import React, { useEffect, useState, useRef } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import BarcodeScanner from "../../../../Components/BarcodeScanner/BarcodeScanner";
import { playScanBeep } from "../../../../Components/BarcodeScanner/playScanBeep";
import useBarcodeLookup from "../../../../api/useBarcodeLookup";
import {
  addOrIncrementStockItem,
  sumItemTotals,
  tryAddStockScan,
  showStockQtyAlert,
  buildStockScanPayload,
  createScanQueue,
} from "../utils/scanCart";

const getCurrentDate = () => new Date().toISOString().split("T")[0];

const MedicineOrder = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const { data: stockMedicines } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/`,
    token,
    { staleTime: 60_000, placeholderData: (previousData) => previousData },
  );

  const [input, setInput] = useState({
    date: getCurrentDate(),
    customer_name: "",
    items: [],
    total_price: 0,
  });

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");

  const {
    handleAdd: handleAddOrder,
    resetForm,
    loading,
  } = useAdd(
    "medicine-orders",
    token,
    t("pharmacy.medicineOrderCreatedSuccess"),
    t("pharmacy.failedToCreateOrder"),
  );

  const autocompleteRef = useRef(null);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);
  const itemsRef = useRef([]);
  const scanQueueRef = useRef(createScanQueue());

  const navigate = useNavigate();
  const location = useLocation();
  const { lookup } = useBarcodeLookup(token);

  useEffect(() => {
    itemsRef.current = input.items;
  }, [input.items]);

  useEffect(() => {
    if (resetForm) {
      setInput({
        date: getCurrentDate(),
        customer_name: "",
        items: [],
        total_price: 0,
      });
      setFormErrors({});
    }
  }, [resetForm]);

  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];

    if (name === "quantity") {
      const max = parseFloat(items[index].available_stock) || 0;
      const qty = parseFloat(value) || 0;
      if (max > 0 && qty > max) {
        items[index].quantity = max;
        showStockQtyAlert(
          { reason: "exceeds_stock", available: max },
          items[index].name,
          t,
          toast,
        );
      } else {
        items[index].quantity = value;
      }
    } else {
      items[index][name] = value;
    }

    if (name === "quantity" || name === "sell_price") {
      const quantity = parseFloat(items[index].quantity) || 0;
      const sellPrice = parseFloat(items[index].sell_price) || 0;
      items[index].total_price = parseFloat((quantity * sellPrice).toFixed(2));
    }

    setInput({ ...input, items });
    updateTotalBill(items);
  };

  const handleAddNewItem = (medicine, quantity = 1, options = {}) => {
    if (!medicine) return;
    setInput((prevInput) => {
      const newItems = addOrIncrementStockItem(
        prevInput.items,
        medicine,
        quantity,
      );
      return {
        ...prevInput,
        items: newItems,
        total_price: sumItemTotals(newItems),
      };
    });
    setAutocompleteInputValue("");
    if (!options.keepScanFocus && autocompleteRef.current) {
      autocompleteRef.current.focus();
    }
  };

  const handleScan = (barcode) => {
    scanQueueRef.current(async () => {
      const data = await lookup(barcode, {
        stockList: stockMedicines || [],
      });
      if (!data.found) {
        playScanBeep(false);
        toast.error(
          data.status === "error"
            ? t("pharmacy.barcodeLookupFailed")
            : t("pharmacy.barcodeNotFound", { barcode }),
        );
        return;
      }

      const medicineName =
        data.medicine?.name || data.best_stock?.medicine_display || barcode;
      const stockPayload = buildStockScanPayload(
        data.best_stock,
        data.barcode || barcode,
        stockMedicines,
      );

      if (!data.best_stock || stockPayload.available_stock <= 0) {
        playScanBeep(false);
        toast.error(t("pharmacy.barcodeOutOfStock", { name: medicineName }));
        return;
      }

      const result = tryAddStockScan(itemsRef.current, stockPayload, 1);
      if (!result.ok) {
        playScanBeep(false);
        showStockQtyAlert(result.check, medicineName, t, toast);
        return;
      }

      itemsRef.current = result.items;
      setInput((prevInput) => ({
        ...prevInput,
        items: result.items,
        total_price: sumItemTotals(result.items),
      }));

      playScanBeep(true);
      toast.success(t("pharmacy.barcodeAddedToSale", { name: medicineName }), {
        toastId: `sale-${data.barcode || barcode}`,
      });
    });
  };

  const appliedScansRef = useRef(false);
  useEffect(() => {
    const scanned = location.state?.scannedItems;
    if (!scanned?.length || appliedScansRef.current) return;
    appliedScansRef.current = true;
    setInput((prevInput) => {
      let items = prevInput.items;
      scanned.forEach((medicine) => {
        items = addOrIncrementStockItem(
          items,
          medicine,
          medicine.scan_quantity || 1,
        );
      });
      return {
        ...prevInput,
        items,
        total_price: sumItemTotals(items),
      };
    });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    setInput({ ...input, items });
    updateTotalBill(items);
  };

  const updateTotalBill = (items) => {
    const totalPrice = items.reduce(
      (sum, item) => sum + (parseFloat(item.total_price) || 0),
      0,
    );

    setInput((prevInput) => ({
      ...prevInput,
      total_price: parseFloat(totalPrice.toFixed(2)),
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (!input.customer_name?.trim()) {
      errors.customer_name = t("pharmacy.customerNameRequired");
      isValid = false;
    }

    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);
    if (isValid) {
      const payload = {
        date: input.date,
        customer_name: input.customer_name.trim(),
        items: input.items.map((item) => ({
          stock_medicine: item.stock_medicine,
          quantity: item.quantity,
        })),
      };
      handleAddOrder(payload);
    }
  };

  const handleKeyDown = (index, fieldType, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (fieldType === "quantity") {
        if (priceRefs.current[index]) {
          priceRefs.current[index].focus();
        }
      } else if (fieldType === "sell_price") {
        if (index < input.items.length - 1) {
          if (quantityRefs.current[index + 1]) {
            quantityRefs.current[index + 1].focus();
          }
        } else {
          if (autocompleteRef.current) {
            autocompleteRef.current.focus();
          }
        }
      }
    }
  };

  const stockList = Array.isArray(stockMedicines)
    ? stockMedicines
    : stockMedicines?.results || [];
  const filteredMedicines =
    autocompleteInputValue.length > 0
      ? stockList.filter((medicine) =>
          (medicine.medicine_display || "")
            .toLowerCase()
            .startsWith(autocompleteInputValue.toLowerCase()),
        )
      : [];

  return (
    <Paper
      sx={{
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
        borderRadius: "16px",
        mt: 4,
        mb: 4,
        p: 6,
        width: "100%",
        mx: "auto",
      }}
    >
      {/* Go Back Icon and Title */}
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={t("pharmacy.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("pharmacy.goBack")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography fontWeight={600} variant="h4" color="primary" gutterBottom>
          {t("pharmacy.orderMedicines")}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("pharmacy.customerName")}
            name="customer_name"
            size="medium"
            variant="outlined"
            value={input.customer_name}
            onChange={(e) =>
              setInput({ ...input, customer_name: e.target.value })
            }
            error={Boolean(formErrors.customer_name)}
            helperText={formErrors.customer_name}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("pharmacy.orderDate")}
            name="date"
            type="date"
            size="medium"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={input.date}
            onChange={(e) => setInput({ ...input, date: e.target.value })}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Search Medicine Section */}
      <Box mb={4}>
        <Typography variant="subtitle1" fontWeight={500} mb={2}>
          {t("pharmacy.addMedicine")}
        </Typography>
        <Box mb={2}>
          <BarcodeScanner onScan={handleScan} listenHardware />
        </Box>
        <Autocomplete
          ref={autocompleteRef}
          value={null}
          inputValue={autocompleteInputValue}
          onInputChange={(event, newInputValue) => {
            setAutocompleteInputValue(newInputValue);
          }}
          onChange={(event, value) => {
            handleAddNewItem(value);
            setAutocompleteInputValue("");
          }}
          options={filteredMedicines}
          getOptionLabel={(option) => option.medicine_display}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                padding: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
                backgroundColor: "#f9f9f9",
                borderRadius: 1,
                "&:hover": {
                  backgroundColor: "#eef2f5",
                },
              }}
            >
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ whiteSpace: "nowrap", color: "text.primary" }}
              >
                {option.medicine_display}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.category")}:</strong>{" "}
                {option.category_display}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.price")}:</strong> {option.sell_price}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "nowrap",
                  color: "text.secondary",
                }}
              >
                <strong>{t("pharmacy.expiryDate")}:</strong>{" "}
                {option.expiry_date}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("pharmacy.searchMedicine")}
              variant="outlined"
              placeholder={t("pharmacy.searchMedicinePlaceholder")}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
              }}
            />
          )}
        />
      </Box>

      {/* Medicine List Table */}
      <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#eef2f5" }}>
            <TableRow>
              <TableCell>{t("pharmacy.medicineName")}</TableCell>
              <TableCell align="center">
                {t("pharmacy.availableStock")}
              </TableCell>
              <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
              <TableCell align="center">{t("pharmacy.price")}</TableCell>
              <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
              <TableCell align="center">{t("pharmacy.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {input.items.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{item.name}</TableCell>
                <TableCell align="center">{item.available_stock}</TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (quantityRefs.current[index] = el)}
                    name="quantity"
                    type="number"
                    size="small"
                    variant="outlined"
                    value={item.quantity}
                    onChange={(e) => handleChangeItemField(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, "quantity", e)}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    inputRef={(el) => (priceRefs.current[index] = el)}
                    name="sell_price"
                    type="number"
                    size="small"
                    variant="outlined"
                    inputProps={{ step: "0.01", min: "0" }}
                    value={item.sell_price}
                    onChange={(e) => handleChangeItemField(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, "sell_price", e)}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={600}>{item.total_price}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={t("pharmacy.removeItem")} arrow>
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Save Button and Total Price Display */}
      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <TextField
          size="small"
          label={t("pharmacy.totalPrice")}
          type="number"
          value={input.total_price}
          disabled
          sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
        />
        <Button
          size="small"
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            fontWeight: 700,
            backgroundColor: "primary.main",
            "&:hover": {
              backgroundColor: "primary.dark",
            },
          }}
        >
          {loading ? t("pharmacy.saving") : t("pharmacy.savePos")}
        </Button>
      </Stack>
    </Paper>
  );
};

export default MedicineOrder;
